import React, { useContext } from 'react';
import axios from 'axios';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import List from '@material-ui/core/List';
import Divider from '@material-ui/core/Divider';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import Avatar from '@material-ui/core/Avatar';
import TextField from '@material-ui/core/TextField';
import AppContext, { Product } from '../../../contexts/AppContext';
import { DispatchActions } from '../../../enums/DispatchActions';
import { config } from '../../../config/config';
import { v4 as uuidv4 } from 'uuid';

const DrawerList = () => {
	const appContext = useContext(AppContext);
	const { cart, totalAmount } = appContext.state;

	const handleQuantityChange = (e: any, productId: number) => {
		const currentProductQuantity = e.target.value;
		const productIndex = cart.findIndex((prd: Product) => prd.id === productId);

		// If quantity is 0, automatically remove product from cart
		if (currentProductQuantity === '0') {
			removeFromCart(productIndex);

			return;
		}

		// Update cart product quantity
		cart[productIndex].quantity = currentProductQuantity;

		// Update cart products
		appContext.dispatch({ type: DispatchActions.UPDATE_CART, payload: cart });

		appContext.dispatch({
			type: DispatchActions.UPDATE_TOTAL_AMOUNT,
		});
	};

	const removeFromCart = (index: number) => {
		// Remove product from cart
		cart.splice(index, 1);

		// Update cart products
		appContext.dispatch({ type: DispatchActions.UPDATE_CART, payload: cart });

		// Subtract product price from total amount
		appContext.dispatch({
			type: DispatchActions.UPDATE_TOTAL_AMOUNT,
		});
	};

	const createCheckout = () => {
		if (cart.length === 0) {
			alert('Please add items to your cart.');

			return;
		}

		const truncatedItems = cart.map((product: Product) => {
			const { code, description, itemName, price, quantity } = product;

			const descriptionMaxNumOfChars = 120;
			const truncatedDescription = description.substring(
				0,
				descriptionMaxNumOfChars
			);

			return {
				name: itemName,
				code,
				description: truncatedDescription,
				quantity: quantity.toString(),
				amount: {
					value: price.toString(),
				},
			};
		});

		const reqBody = {
			totalAmount: {
				currency: 'PHP',
				value: totalAmount.toString(),
			},
			buyer: {
				firstName: 'Craig',
				lastName: 'Smith',
				contact: {
					phone: '09485739123',
					email: 'craigsmith@yahoo.com',
				},
				shippingAddress: {
					line1: '737 Saint James Drive',
					city: 'Makati',
					state: 'Manila',
					postalCode: '1228',
					alphaCountryCode: 'PHL',
				},
			},
			items: truncatedItems,
			redirectUrls: {
				success: `${config.MERCHANT_URL}/transaction/success`,
				failure: `${config.MERCHANT_URL}/transaction/failed`,
			},
			requestReferenceNumber: uuidv4(),
		};

		const merchantCredentials = `${config.MERCHANT_CREDENTIALS.SUB_MERCHANT_ID}:${config.MERCHANT_CREDENTIALS.AUTH_KEY}`;
		const base64MerchantCredentials =
			Buffer.from(merchantCredentials).toString('base64');

		const reqConfig = {
			headers: { Authorization: `Basic ${base64MerchantCredentials}` },
		};

		axios
			.post(`${config.SERVER_URL}/v1/checkouts`, reqBody, reqConfig)
			.then(function (res) {
				const { data } = res;
				const { redirectUrl } = data;

				window.location.href = redirectUrl;
			})
			.catch((err) => {
				const errorInfo = err.response?.data;

				if (errorInfo?.status === 'Declined') {
					alert(errorInfo.error);
				} else {
					alert(err.message);
				}
			});
	};

	return (
		<Box sx={{ width: 340 }} role='presentation'>
			<List>
				<ListItem>
					<Typography variant='h6'>Order Summary</Typography>
				</ListItem>
			</List>

			<Divider />

			{/* Products */}
			<List sx={{ width: '100%', maxWidth: 360, bgcolor: 'background.paper' }}>
				{cart.length === 0 && (
					<ListItem>
						<ListItemText secondary='You have no items in your cart.' />
					</ListItem>
				)}

				{cart.length !== 0 &&
					cart.map((product: Product) => {
						const { id, path, itemName, price, quantity } = product;

						return (
							<ListItem
								key={product.id}
								secondaryAction={
									<TextField
										id='filled-number'
										type='number'
										InputLabelProps={{
											shrink: true,
										}}
										variant='outlined'
										size='small'
										sx={{ width: 80 }}
										value={quantity}
										onChange={(e) => handleQuantityChange(e, id)}
									/>
								}
							>
								<ListItemAvatar>
									<Avatar src={path} />
								</ListItemAvatar>
								<ListItemText primary={itemName} secondary={`PHP ${price}`} />
							</ListItem>
						);
					})}
			</List>

			<Divider />

			{/* Total */}
			<List>
				<ListItem>
					<Box
						sx={{
							display: 'flex',
							width: '100%',
						}}
					>
						<Box sx={{ flexGrow: 1 }}>
							<Typography variant='body1'>Total</Typography>
						</Box>
						<Box>
							<Typography variant='body1'>PHP {totalAmount}</Typography>
						</Box>
					</Box>
				</ListItem>
			</List>

			{/* Checkout Button */}
			<List>
				<ListItem>
					<Button
						variant='contained'
						sx={{ width: '100%' }}
						onClick={createCheckout}
					>
						Checkout
					</Button>
				</ListItem>
			</List>
		</Box>
	);
};

export default DrawerList;
